// Refund-flow bootstrap helpers.
//
// /copy-with-refund returns a Receipt whose items often lack `main_image`
// (the BE doesn't always populate it for refund clones), and /cart/preview
// rejects negative-qty refund carts — so the FE has to do two things the
// normal sale flow gets for free:
//
//   1. Enrich each refund item with a product image via the catalog
//      detail endpoint, so the cart row renders artwork instead of the
//      placeholder tile.
//   2. Seed `cartStore.preview` with the BE-supplied totals so the cart
//      summary's tax / total rows show real numbers (otherwise the
//      cashier sees "Calculating tax…" the entire refund flow).
//
// Both callers (FunctionsTab.handleRefundReceiptSelected and
// OrderDetailModal.handleRefund) use this helper to avoid drift between
// the two refund entry points.

import type { QueryClient } from '@tanstack/react-query'
import { productKeys } from '@/api/products'
import { v2, v2api } from '@/api/v2'
import { useCartStore } from '@/stores/cartStore'
import { usePOSStore } from '@/stores/posStore'
import { useSessionStore } from '@/stores/sessionStore'
import { extractTaxes } from '@/lib/utils'
import type { Product } from '@/types'

interface RefundItem {
  product_id: number
  main_image?: string | null
  image_url?: string | null
  image?: string | null
}

interface RefundResponseTotals {
  subtotal?: number | string
  taxes?: number | string
  tax?: number | string
  total?: number | string
  discount_amount?: number | string
  deposit_amount?: number | string | null
  recycling_amount?: number | string | null
}

/** Fetch THIS store's catalog row for each unique product_id in the
 *  items list, by exact id. Returns a map keyed by product_id; misses
 *  resolve to `null` so the caller falls back to the placeholder tile.
 *
 *  Exact id, no fallbacks -- twice deliberate:
 *
 *   - The lookup used to search the item's SKU as free text
 *     (`q: sku`). SKUs are not unique across the catalogue and the
 *     endpoint matches names/descriptions too, so a miss could enrich a
 *     refund line with a DIFFERENT product's artwork and price.
 *   - It then fell back to the unscoped /objects/products/{id} master
 *     row, which carries net price and no per-store stock.
 *
 *  The cache key is store-scoped for that second reason: writing a
 *  master row into the plain `productKeys.detail(id)` key poisoned it
 *  for every other reader (5-minute staleTime, first writer wins),
 *  which is how OrderDetailModal's "buy again" could read "no stock" on
 *  a stocked product. Same key shape that modal uses. */
export async function fetchProductsForRefundItems(
  items: RefundItem[],
  queryClient: QueryClient,
): Promise<Map<number, Product | null>> {
  const out = new Map<number, Product | null>()
  // Register's store -- part of the key, since the row is that store's
  // price and on-hand. Null (no store selected) still keys distinctly.
  const storeId = useSessionStore.getState().selectedStore?.id ?? null

  const ids = new Set<number>()
  for (const it of items) {
    if (typeof it.product_id !== 'number') continue
    ids.add(it.product_id)
  }

  await Promise.all(
    Array.from(ids).map(async (id) => {
      try {
        const product = await queryClient.fetchQuery<Product | null>({
          queryKey: [...productKeys.detail(id), 'store-catalog', storeId],
          queryFn: async () => {
            const data = await v2api.get<{ products?: Product[] } | Product[]>(
              v2('/products/search'),
              { id, per_page: 5 },
            )
            const list: Product[] = Array.isArray(data)
              ? data
              : Array.isArray(data?.products)
                ? data!.products!
                : []
            return list.find((p) => Number(p.id) === id) ?? null
          },
          staleTime: 1000 * 60 * 5,
        })
        out.set(id, product)
      } catch {
        out.set(id, null)
      }
    }),
  )
  return out
}

/** Stamp `main_image` onto each refund item using the matching product's
 *  image_url / thumbnail_url / image — whichever resolves first. Items
 *  whose product can't be loaded keep their original (likely null) image
 *  field; loadRefundItems falls back to the placeholder tile in that case. */
export function attachProductImages(
  items: RefundItem[],
  productsById: Map<number, Product | null>,
): RefundItem[] {
  return items.map((it) => {
    if (it.main_image || it.image_url || it.image) return it
    const product = productsById.get(it.product_id)
    if (!product) return it
    const image =
      product.image_url
      ?? product.thumbnail_url
      ?? product.image
      ?? null
    if (!image) return it
    return { ...it, main_image: image }
  })
}

/** Re-seed posStore.serverTotals from a Receipt response after a refund
 *  item mutation (PUT qty / DELETE line). The BE returns refund totals
 *  with positive magnitudes; the cart's serverTotals uses negative for
 *  refunds, so we flip the sign here. Tax aggregation uses extractTaxes
 *  to cover Canada multi-jurisdiction (tax1..tax4) receipts that omit
 *  the aggregated `taxes` field.
 *
 *  Called from anywhere a refund-item mutation lands a fresh Receipt:
 *  ProductModal's lot/qty edits, CartItemRow's X / +/− in refund mode,
 *  and any future refund-aware path. Keeping the math in one place
 *  prevents drift between callers. */
export function reseedRefundTotalsFromReceipt(
  receipt: { totals?: Record<string, unknown>; amount_to_pay?: unknown },
): void {
  const t = (receipt.totals ?? {}) as Record<string, unknown>
  const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
    return Number.isFinite(n) ? n : 0
  }
  usePOSStore.getState().setServerTotals({
    subtotal: -Math.abs(num(t.subtotal)),
    taxes: -Math.abs(extractTaxes(t as { taxes?: number | string; tax?: number | string })),
    deposit: -Math.abs(num((t as { deposit_amount?: unknown }).deposit_amount)),
    total: -Math.abs(num(t.total)),
    amountToPay: -Math.abs(num(receipt.amount_to_pay ?? t.total)),
  })
}

/** Seed cartStore.preview from the refund-create response so CartSummary
 *  picks up real subtotal / tax / total values right away. Without this
 *  the summary stays in its "Calculating tax…" tier — /cart/preview is
 *  intentionally skipped in refund mode (rejects negative qty), so this
 *  is the only seed the cart gets until checkout. */
export function seedRefundPreview(refund: {
  id?: number
  number?: string
  amount_to_pay?: number | string
  totals?: RefundResponseTotals
}): void {
  const t = (refund.totals ?? {}) as RefundResponseTotals & Record<string, unknown>
  const numStr = (v: unknown, fallback = '0'): string => {
    if (v === null || v === undefined) return fallback
    return String(v)
  }
  // BE refund response returns tax fragmented across tax1..tax4 fields when
  // the aggregated `taxes` is absent (Canada multi-jurisdiction receipts).
  // Use the shared helper so the CFD picks up tax in those cases too.
  const taxes = extractTaxes(t)
  useCartStore.setState({
    preview: {
      id: refund.id ?? 0,
      number: refund.number ?? '',
      totals: {
        subtotal: numStr(t.subtotal),
        taxes: taxes === 0 ? numStr(t.taxes ?? t.tax) : String(taxes),
        total: numStr(t.total),
        discount_amount: numStr(t.discount_amount),
        deposit_amount: t.deposit_amount == null ? null : String(t.deposit_amount),
        recycling_amount: t.recycling_amount == null ? null : String(t.recycling_amount),
        amount_to_pay: numStr(refund.amount_to_pay ?? t.total),
      },
      amount_to_pay: numStr(refund.amount_to_pay ?? t.total),
      items: [],
      warnings: [],
    },
    previewStatus: 'idle',
    previewedAt: Date.now(),
  })
}
